/**
 * The browser audio surface, behind an injectable seam.
 *
 * Two reasons this is an interface rather than direct global access:
 *
 * 1. **Testability.** `MediaRecorder`, `MediaStream` and `getUserMedia` do not exist under Vitest's
 *    node environment and cannot be meaningfully polyfilled. The AUD-3 contract — *every* exit
 *    routes through one finalize, chunks persist before finalize, the recorder is never wedged — is
 *    logic worth proving in Node, even though the *container* question it serves can only be
 *    answered on the device. A fake platform lets the logic be tested without pretending the
 *    measurement was.
 *
 * 2. **Honesty about what is being measured.** Everything host-specific is in `browserAudioPlatform`
 *    below, in one place a reader can check against what iOS actually does.
 *
 * These interfaces are deliberately narrower than the DOM's. They name only what build unit U-1
 * touches, so a reader can see the whole surface the spike depends on in one screen.
 */

export type TrackReadyState = 'live' | 'ended' | string;

export interface MediaStreamTrackLike {
  readonly kind: string;
  readonly label: string;
  readonly readyState: TrackReadyState;
  stop(): void;
  addEventListener(type: 'ended', listener: () => void): void;
  removeEventListener(type: 'ended', listener: () => void): void;
}

export interface MediaStreamLike {
  getTracks(): MediaStreamTrackLike[];
  getAudioTracks(): MediaStreamTrackLike[];
}

/** `MediaRecorder`'s `dataavailable`, reduced to the one field that matters. */
export interface RecorderDataEvent {
  data: Blob;
}

export type RecorderState = 'inactive' | 'recording' | 'paused' | string;

export interface MediaRecorderLike {
  readonly state: RecorderState;
  /** AUD-1 — what the blob must be stored with. Read after `start`, never guessed. */
  readonly mimeType: string;
  start(timesliceMs?: number): void;
  stop(): void;
  ondataavailable: ((event: RecorderDataEvent) => void) | null;
  onstart: (() => void) | null;
  onstop: (() => void) | null;
  onerror: ((event: unknown) => void) | null;
}

export type MicPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported' | 'error';

export interface AudioPlatform {
  hasMediaRecorder: boolean;
  hasGetUserMedia: boolean;
  isTypeSupported(mime: string): boolean;
  /** @param mime `null` lets the browser choose, which is what AUD-1 wants reported. */
  createRecorder(stream: MediaStreamLike, mime: string | null): MediaRecorderLike;
  getUserMedia(): Promise<MediaStreamLike>;
  queryMicrophonePermission(): Promise<MicPermissionState>;
  now(): number;
}

interface MediaRecorderCtor {
  new (stream: unknown, options?: { mimeType?: string }): MediaRecorderLike;
  isTypeSupported?: (mime: string) => boolean;
}

/**
 * The real thing.
 *
 * The casts here are the whole reason this file exists: they are confined to one adapter, where a
 * reader can see exactly which host APIs the spike touches and with what arguments. `{ audio: true }`
 * with no further constraints is deliberate — the product has no reason to ask for echo
 * cancellation or a sample rate, and adding constraints would measure a stream the product will
 * never request.
 */
export function browserAudioPlatform(): AudioPlatform {
  const g = globalThis as unknown as {
    MediaRecorder?: MediaRecorderCtor;
    navigator?: Navigator;
    performance?: { now(): number };
  };

  const ctor = g.MediaRecorder;
  const md = g.navigator?.mediaDevices;

  return {
    hasMediaRecorder: typeof ctor === 'function',
    hasGetUserMedia: typeof md?.getUserMedia === 'function',

    isTypeSupported(mime) {
      if (typeof ctor?.isTypeSupported !== 'function') return false;
      try {
        return ctor.isTypeSupported(mime);
      } catch {
        // A throwing `isTypeSupported` means "cannot answer", which is not "supported". Not a
        // silent catch: the false is the answer, and the support matrix records it as unsupported.
        return false;
      }
    },

    createRecorder(stream, mime) {
      if (typeof ctor !== 'function') {
        throw new Error('MediaRecorder is not available in this browser.');
      }
      return mime === null ? new ctor(stream) : new ctor(stream, { mimeType: mime });
    },

    async getUserMedia() {
      if (typeof md?.getUserMedia !== 'function') {
        throw new Error('navigator.mediaDevices.getUserMedia is not available in this browser.');
      }
      const stream = await md.getUserMedia({ audio: true });
      return stream as unknown as MediaStreamLike;
    },

    async queryMicrophonePermission() {
      const perms = g.navigator?.permissions;
      if (!perms || typeof perms.query !== 'function') return 'unsupported';
      try {
        // Safari has historically not accepted 'microphone' here and throws a TypeError. That is a
        // capability answer, not a failure, so it maps to 'unsupported' below.
        const status = await perms.query({ name: 'microphone' as PermissionName });
        return status.state;
      } catch (cause) {
        return cause instanceof TypeError ? 'unsupported' : 'error';
      }
    },

    now() {
      return g.performance?.now() ?? Date.now();
    },
  };
}
